import {
  customElement,
  DeesElement,
  html,
  css,
  cssManager,
  property,
  state,
  type TemplateResult,
} from '@design.estate/dees-element';
import { demoFunc } from './dees-tile.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-tile': DeesTile;
  }
}

/**
 * dees-tile — the unified "rounded on rounded" tile frame.
 *
 * RESPONSIBILITIES (what this component owns):
 *  1. Outer card — border, border-radius, background, overflow clipping
 *  2. Flex column layout — stacking header / content / footer vertically
 *  3. Content inset — the rounded inner area with border-top and border-bottom
 *  4. Default heading — styled 32px heading text when no slot="header" is provided
 *  5. Footer visibility — auto-hides footer area when slot="footer" is empty
 *
 * NOT RESPONSIBILITIES (what consumer components own):
 *  - Header/footer height, padding, font-size, colors when using custom slots
 *  - Content layout (absolute, flex, grid — consumer decides)
 *  - Any semantic meaning of header/footer content
 *
 * The header and footer slots are BARE containers (just flex-shrink: 0).
 * The slotted content fully controls its own appearance.
 * Only the default heading fallback (.tile-heading) carries tile-level styling.
 *
 * FRAMELESS: `frameless` drops the outer border and corner radius — and nothing else — so a
 * host that already owns the surface (a modal, a preview stage, a transcript row) gets the
 * tile's layout without a second frame. Background, header, content separators and footer
 * stay exactly as they are. Every tile-based component forwards its own `frameless` here.
 */
@customElement('dees-tile')
export class DeesTile extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Layout'];

  @property({ type: String })
  accessor heading: string = '';

  @property({ type: String, reflect: true })
  accessor overscroll: 'contain' | 'auto' | 'none' = 'auto';

  /** Drop the outer border and corner radius: the host already owns the surface. */
  @property({ type: Boolean, reflect: true })
  accessor frameless: boolean = false;

  @state()
  accessor hasFooter: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        font-family: var(--dees-font-family);
        color: var(--dees-color-text-primary);
      }

      /* --- The frame --- */
      .tile-outer {
        position: relative;
        flex: 1;
        min-height: 0;
        background: var(--dees-tile-surface, var(--dees-color-bg-primary));
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-xl);
        corner-shape: var(--dees-corner-shape);
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      /* The content keeps its hairline dividers: only the frame itself goes. */
      :host([frameless]) .tile-outer {
        border: 0;
        border-radius: 0;
      }

      /* --- Header: bare container, only the default heading gets styled --- */
      .tile-header {
        flex-shrink: 0;
      }

      .tile-heading {
        height: var(--dees-control-height-sm);
        line-height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-control-size);
        font-weight: var(--dees-font-control-weight);
        letter-spacing: -0.01em;
        color: var(--dees-color-text-primary);
      }

      /* --- Content: the rounded inset ---
         Uses overflow-y: auto so that when a consumer (e.g. dees-modal) caps
         the tile with max-height, long content scrolls inside the tile
         instead of being clipped. For consumers without max-height
         (e.g. dees-stepper), the tile grows with content and the scroll
         never activates. Horizontal overflow stays clipped to preserve the
         rounded corners. */
      .tile-content {
        flex: 1;
        position: relative;
        /* No own radius: the .tile-outer frame (overflow:hidden + radius) rounds
           all outer corners. The content only carries straight header/footer
           hairline dividers — rounding here would round the divider ends and
           show corners mid-tile below the header. */
        border-top: 1px solid var(--dees-color-border-subtle);
        border-bottom: 1px solid var(--dees-color-border-subtle);
        overflow-x: hidden;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
      }

      :host([overscroll="contain"]) .tile-content {
        overscroll-behavior: contain;
      }

      .tile-content.no-footer {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }

      /* --- Footer: bare container, consumer styles the slotted content --- */
      .tile-footer {
        flex-shrink: 0;
      }

      .tile-footer.hidden {
        display: none;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="tile-outer" part="outer">
        <div class="tile-header" part="header">
          <slot name="header">
            <div class="tile-heading">${this.heading}</div>
          </slot>
        </div>
        <div class="tile-content ${!this.hasFooter ? 'no-footer' : ''}" part="content">
          <slot></slot>
        </div>
        <div class="tile-footer ${!this.hasFooter ? 'hidden' : ''}" part="footer">
          <slot name="footer" @slotchange=${this.onFooterSlotChange}></slot>
        </div>
      </div>
    `;
  }

  private onFooterSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this.hasFooter = slot.assignedNodes({ flatten: true }).length > 0;
  }
}
